import { isTeamAdmin } from '@bb/users';
import type { AuthContextType } from 'contexts';
import isAuthForLocation from './isAuthForLocation';

interface IsAuthForRoleProps {
  auth: AuthContextType['auth'];
  org?: string;
  location?: string | number;
  locations?: string[];
  teams?: (string | undefined)[];
  reps?: { id: string; team?: string | null; teams?: (string | null)[] }[];
  hiddenReps?: { id: string; team?: string; teams?: string }[];
}

/**
 * A utility function to check whether the passed user (auth) is
 * authorized to manage the passed org, location, or team.
 * @param param0 object with data:
 * auth: The user we are checking the authorization of.
 * org (optional): The org to check if they are authorized over.
 * location (optional): The branch location to check if they are authorized over.
 * locations (optional): An array of branch locations to check if they are authorized over at least one.
 * teams (optional): An array of teams to check if they are authorized over at least one.
 * reps (optional): An array of listing representative users to check if auth is authorized over at least one.
 * @returns boolean if the user had permissions over any of the passed org/locations/teams/reps
 */
const isAuthForRole = ({
  auth,
  org,
  location,
  locations = [],
  teams = [],
  reps = [],
}: IsAuthForRoleProps): boolean => {
  // The org we are checking must match the user's.
  if (org && auth?.org?.id !== org) return false;
  // Global admins always have permission for their org.
  if (auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN) return true;
  // Branch admins can manage their own branches.
  if (
    auth.roles[auth.role] === auth.roles.BRANCH_ADMIN &&
    isAuthForLocation(auth, { location, locations })
  ) {
    return true;
  }

  // Make an array of all the teams passed in through both params
  const repTeams = reps
    .flatMap(r => r.teams)
    .concat(teams)
    .filter(Boolean)
    .map(t => t!.toString());
  // Check if the user shares any teams with the passed teams
  const hasRelatedTeamReps = repTeams.some(team =>
    auth.teams.some(x => String(x) === team),
  );
  // Team Admins can manage their own teams.
  if (isTeamAdmin(auth) && hasRelatedTeamReps) {
    return true;
  }

  // The user themself WAS one of the passed listing reps.
  return reps?.some(({ id }) => id === auth.id);
};

export default isAuthForRole;
